import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { finalize, catchError, pairwise, takeUntil } from 'rxjs/operators';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PagedGroupTreeNodeResponse } from '../../hickory-api/model/pagedGroupTreeNodeResponse';
import { GroupTreeNode, PagedGroups } from 'src/app/hickory-api';
import { GroupsApi } from '../../hickory-api/api/groups.service';
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { tap, debounceTime, Subscription, Subject } from 'rxjs';
import { FormControl } from '@angular/forms';
import { AuthService } from 'src/app/shared/authentication/auth.service';

interface GroupNode {
  group: GroupTreeNode;
  indentLevel: number;
  expanded?: boolean;
  loading?: boolean;
}

export interface GroupsSelectDialogData {
  existingGroups?: GroupTreeNode[];
  existingGroupIds?: number[];
  classification?: 'Public' | 'Internal';
  tenantId?: number;
  hideClassification?: boolean;
}

@Component({
  selector: 'app-groups-select-dialog',
  templateUrl: './groups-select-dialog.component.html',
  styleUrls: ['./groups-select-dialog.component.scss'],
  standalone: false
})
export class GroupsSelectDialogComponent implements OnInit, OnDestroy {
  topLevelGroups: { [key: string]: GroupNode[] } = {
    Public: [],
    Internal: [],
    All: [],
  };

  parentToChildrenMap: Map<number, GroupNode[]> = new Map<number, GroupNode[]>();

  selectedGroupNodes: GroupNode[] = [];

  classification: FormControl<'All' | 'Public' | 'Internal'> = new FormControl('All');
  searchControl: FormControl<string> = new FormControl();

  isLoading = false;
  hasLoadError = false;
  destroy$: Subject<void> = new Subject();

  get canAssignToParentGroups(): boolean { return this.authService.hasPermission('hickory.parent-groups-membership'); }
  get selectedGroups(): GroupTreeNode[] { return this.selectedGroupNodes.map((group) => group.group); }
  get tenantId(): number { return this.data.tenantId ?? this.authService.user.snapshot.tenantId; }

  constructor(
    private readonly groupsApi: GroupsApi,
    private readonly authService: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: GroupsSelectDialogData,
    @Inject(L10N_LOCALE) public locale: L10nLocale
  ) {}

  ngOnInit(): void {
    this.getExistingGroups();

    this.searchControl.valueChanges
      .pipe(
        tap(() => this.requestRx?.unsubscribe()),
        debounceTime(300),
        tap((value: string) => {
          if (!value) {
            this.getParentGroups(this.classification.value, true);
          } else {
            this.onSearch(value, this.classification.value);
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.classification.valueChanges
      .pipe(
        pairwise(),
        tap(([prev, curr]) => {
          if (this.searchControl.value) {
            this.searchControl.setValue('', { emitEvent: false });
            this.topLevelGroups[prev] = [];
          }

          this.onClassificationChange(curr);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.classification.setValue(this.data.classification ?? 'All');
    this.getParentGroups(this.classification.value, true);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onClassificationChange(classification: 'All' | 'Public' | 'Internal'): void {
    this.getParentGroups(classification === 'All' ? null : classification);
  }

  requestRx: Subscription;
  onSearch(value: string, classification: 'Public' | 'Internal' | 'All' | null = this.classification.value): void {
    if (classification === 'All') {
      classification = null;
    }

    if (this.requestRx) {
      this.requestRx.unsubscribe();
    }

    this.isLoading = true;
    this.hasLoadError = false;
    this.requestRx = this.groupsApi
      .getGroupsV4(this.tenantId, null, null, 1, 500, value, classification as 'Public' | 'Internal' | null)
      .pipe(
        catchError((error) => {
          this.hasLoadError = true;
          return error;
        }),
        tap((groups: PagedGroups) => {
          const type = classification || 'All';

          this.topLevelGroups[type] = groups.items.map((group) => {
            return { group, indentLevel: 0 };
          });
        }),
        finalize(() => (this.isLoading = false))
      )
      .subscribe();
  }

  getExistingGroups(): void {
    if (this.data.existingGroupIds?.length) {
      this.groupsApi.getGroupsV4(this.tenantId, this.data.existingGroupIds, null, 1, 500).subscribe((groups) => {
        this.selectedGroupNodes.push(...groups.items.map((group) => ({ group, indentLevel: 0 })));
      });
    }

    if (this.data.existingGroups?.length) {
      for (const group of this.data.existingGroups) {
        this.selectedGroupNodes.push({ group: group, indentLevel: 0 });
      }
    }
  }

  getParentGroups(classification: 'Public' | 'Internal' | 'All' | null = this.classification.value, refresh = false): void {
    if (!classification && this.topLevelGroups['All']?.length > 0 && !refresh) {
      return;
    }

    if (!!classification && this.topLevelGroups[classification]?.length > 0 && !refresh) {
      return;
    }

    if (classification === 'All') {
      classification = null;
    }

    this.isLoading = true;
    this.hasLoadError = false;
    this.groupsApi
      .getGroupTreeNodesV4(this.tenantId, null, classification as 'Public' | 'Internal' | null, 1, 500)
      .pipe(
        catchError((error) => {
          this.hasLoadError = true;
          return error;
        }),
        tap((groups: PagedGroupTreeNodeResponse) => {
          const type = classification || 'All';

          groups.items.forEach((group) => {
            if (group.childGroupCount > 0) {
              this.parentToChildrenMap.set(group.id, []);
            }
          });

          this.topLevelGroups[type] = groups.items.map((group) => {
            return { group: group, indentLevel: 0 };
          });
        }),
        finalize(() => (this.isLoading = false))
      )
      .subscribe();
  }

  onToggleGroup(node: GroupNode): void {
    if (this.isGroupSelected(node.group)) {
      this.onRemoveGroup(node);
    } else {
      this.onAddGroup(node);
    }
  }

  onAddGroup(node: GroupNode): void {
    if (this.isGroupSelected(node.group)) {
      return;
    }

    this.selectedGroupNodes.push(JSON.parse(JSON.stringify(node)));
  }

  onRemoveGroup(node: GroupNode): void {
    this.selectedGroupNodes = this.selectedGroupNodes.filter((group) => group.group.id !== node.group.id);
  }

  isGroupSelected(group: GroupTreeNode): boolean {
    return this.selectedGroupNodes.some((node) => node.group.id === group.id);
  }

  onExpandNode(node: GroupNode): void {
    if (node.expanded) {
      node.expanded = false;
      return;
    }

    if (this.hasChildren(node.group) && !this.parentToChildrenMap.get(node.group.id)?.length) {
      node.loading = true;
      this.groupsApi
        .getGroupTreeNodesV4(this.tenantId, node.group.id, this.classification.value === 'All' ? null : this.classification.value, 1, 500)
        .pipe(
          tap((children: PagedGroupTreeNodeResponse) => {
            this.parentToChildrenMap.set(
              node.group.id,
              children.items.map((child) => {
                return { group: child, indentLevel: node.indentLevel + 1 };
              })
            );

            node.expanded = true;
          }),
          finalize(() => (node.loading = false))
        )
        .subscribe();
    } else {
      node.expanded = true;
    }
  }

  getChildren(node: GroupNode): GroupNode[] {
    if (this.parentToChildrenMap.has(node.group.id)) {
      return this.parentToChildrenMap.get(node.group.id);
    }

    return [];
  }

  hasChildren(node: GroupTreeNode): boolean {
    return node.childGroupCount > 0 || this.parentToChildrenMap.has(node.id);
  }

  getIndentLevel(node: GroupNode): string {
    return node.indentLevel * 10 + 'px';
  }
}
